import React, { useContext } from "react";
import { AppContext } from "@context/AppContext";
import useNavigationData from "@staticQueries/mobileNavigation";
import NavLink, { KontentNavigationLink } from "./NavLink";
import PrimaryButton, { KontentPrimaryButton } from "@ui/PrimaryButton";
import Triangle from "./Triangle";

interface DataTypes {
  logo: { value: [{ url: string }] };
  main_links: { value: KontentNavigationLink[] };
  secondary_links: { value: (KontentNavigationLink | KontentPrimaryButton)[] };
}

const Sidebar = () => {
  const { isSidebarOpen } = useContext(AppContext);
  const data: DataTypes = useNavigationData();

  // A function to ensure data type matches PrimaryButton component
  const isPrimaryButton = (
    link: KontentPrimaryButton | KontentNavigationLink
  ): link is KontentPrimaryButton => {
    return link.system?.type === "primary_button";
  };

  return (
    <aside
      className={`fixed z-[99] bg-redWood transition-all duration-300 ${
        isSidebarOpen
          ? "right-0 top-0 h-[calc(100vh-50px)] w-full rounded-[0%] opacity-100"
          : "invisible right-5 top-[18px] h-[50px] w-[50px] rounded-[50%] opacity-0"
      }`}
    >
      {isSidebarOpen && (
        <nav
          className={`flex h-full flex-col items-center justify-center gap-16 transition duration-500 ${
            !isSidebarOpen && "opacity-0"
          }`}
        >
          {/* Rendering Primary Links */}
          <ul className="flex flex-col items-center gap-10">
            {data.main_links.value.map((link, idx) => (
              <li key={idx}>
                <NavLink
                  internalPath={link.elements.internal_path.value}
                  externalUrl={link.elements.external_url.value}
                  new_window={link.elements.new_window.value[0]?.codename}
                  variant="primary"
                >
                  {link.elements.text.value}
                </NavLink>
              </li>
            ))}
          </ul>
          {/* Rendering Secondary Links */}
          <ul className="flex flex-col items-center gap-5">
            {data.secondary_links.value.map((link, idx) =>
              // Checking if link is a primary button, if so render as PrimaryButton
              isPrimaryButton(link) ? (
                <li key={idx}>
                  <PrimaryButton
                    internalText={link.elements.internal_text.value}
                    externalText={link.elements.external_text.value}
                    internalPath={link.elements.internal_path.value}
                    externalUrl={link.elements.external_url.value}
                    new_window={link.elements.new_window.value[0].codename}
                    icon={link.elements.icon?.value[0]?.codename}
                  />
                </li>
              ) : (
                // Otherwise render as a Navlink
                <li key={idx}>
                  <NavLink
                    internalPath={link.elements.internal_path.value}
                    externalUrl={link.elements.external_url.value}
                    new_window={link.elements.new_window.value[0]?.codename}
                    variant="secondary"
                  >
                    {link.elements.text.value}
                  </NavLink>
                </li>
              )
            )}
          </ul>
        </nav>
      )}
      <Triangle
        className={`border-redWood transition duration-100 ${
          !isSidebarOpen && "opacity-0"
        }`}
      />
    </aside>
  );
};

export default Sidebar;
